/**
 * Jurisdiction identity registry.
 *
 * IDENTITY PLUMBING ONLY — this is not the Stage 2 jurisdiction data.
 *
 * It carries three things per jurisdiction: the stable two-letter id the legacy
 * dataset lacks, the formal name, and the display name the existing application
 * already keys on. All three are derived from the application's own data (the
 * keys of `public/data/llc-data.json` and `src/data/lookups.ts`), so no content
 * from either reference document appears here.
 *
 * It exists because partial migration needs a stable key: the build has to know
 * which canonical record corresponds to which legacy key in order to substitute
 * one jurisdiction at a time. The richer `JurisdictionRecord` — filing agency,
 * portals, channel fees — is authored in Stage 2.
 *
 * `legacyKey` is load-bearing in two places. It is the key of the generated
 * legacy dataset, and it is the value `normalizeStateName()` produces, which the
 * map uses to join geometry and to drive hover, selection and fly-to. It must
 * not change.
 */

import type { JurisdictionId } from "./model";

export type JurisdictionIdentity = {
  id: JurisdictionId;
  /** Formal name, which differs from the display name only for the District. */
  name: string;
  /** The key the existing application uses, and the map's geoKey. */
  legacyKey: string;
};

/** All 51 jurisdictions, in the order the existing legacy dataset lists them. */
export const JURISDICTION_IDENTITIES: readonly JurisdictionIdentity[] = [
  { id: "AL", name: "Alabama", legacyKey: "Alabama" },
  { id: "AK", name: "Alaska", legacyKey: "Alaska" },
  { id: "AZ", name: "Arizona", legacyKey: "Arizona" },
  { id: "AR", name: "Arkansas", legacyKey: "Arkansas" },
  { id: "CA", name: "California", legacyKey: "California" },
  { id: "CO", name: "Colorado", legacyKey: "Colorado" },
  { id: "CT", name: "Connecticut", legacyKey: "Connecticut" },
  { id: "DE", name: "Delaware", legacyKey: "Delaware" },
  { id: "FL", name: "Florida", legacyKey: "Florida" },
  { id: "GA", name: "Georgia", legacyKey: "Georgia" },
  { id: "HI", name: "Hawaii", legacyKey: "Hawaii" },
  { id: "ID", name: "Idaho", legacyKey: "Idaho" },
  { id: "IL", name: "Illinois", legacyKey: "Illinois" },
  { id: "IN", name: "Indiana", legacyKey: "Indiana" },
  { id: "IA", name: "Iowa", legacyKey: "Iowa" },
  { id: "KS", name: "Kansas", legacyKey: "Kansas" },
  { id: "KY", name: "Kentucky", legacyKey: "Kentucky" },
  { id: "LA", name: "Louisiana", legacyKey: "Louisiana" },
  { id: "ME", name: "Maine", legacyKey: "Maine" },
  { id: "MD", name: "Maryland", legacyKey: "Maryland" },
  { id: "MA", name: "Massachusetts", legacyKey: "Massachusetts" },
  { id: "MI", name: "Michigan", legacyKey: "Michigan" },
  { id: "MN", name: "Minnesota", legacyKey: "Minnesota" },
  { id: "MS", name: "Mississippi", legacyKey: "Mississippi" },
  { id: "MO", name: "Missouri", legacyKey: "Missouri" },
  { id: "MT", name: "Montana", legacyKey: "Montana" },
  { id: "NE", name: "Nebraska", legacyKey: "Nebraska" },
  { id: "NV", name: "Nevada", legacyKey: "Nevada" },
  { id: "NH", name: "New Hampshire", legacyKey: "New Hampshire" },
  { id: "NJ", name: "New Jersey", legacyKey: "New Jersey" },
  { id: "NM", name: "New Mexico", legacyKey: "New Mexico" },
  { id: "NY", name: "New York", legacyKey: "New York" },
  { id: "NC", name: "North Carolina", legacyKey: "North Carolina" },
  { id: "ND", name: "North Dakota", legacyKey: "North Dakota" },
  { id: "OH", name: "Ohio", legacyKey: "Ohio" },
  { id: "OK", name: "Oklahoma", legacyKey: "Oklahoma" },
  { id: "OR", name: "Oregon", legacyKey: "Oregon" },
  { id: "PA", name: "Pennsylvania", legacyKey: "Pennsylvania" },
  { id: "RI", name: "Rhode Island", legacyKey: "Rhode Island" },
  { id: "SC", name: "South Carolina", legacyKey: "South Carolina" },
  { id: "SD", name: "South Dakota", legacyKey: "South Dakota" },
  { id: "TN", name: "Tennessee", legacyKey: "Tennessee" },
  { id: "TX", name: "Texas", legacyKey: "Texas" },
  { id: "UT", name: "Utah", legacyKey: "Utah" },
  { id: "VT", name: "Vermont", legacyKey: "Vermont" },
  { id: "VA", name: "Virginia", legacyKey: "Virginia" },
  { id: "WA", name: "Washington", legacyKey: "Washington" },
  { id: "WV", name: "West Virginia", legacyKey: "West Virginia" },
  { id: "WI", name: "Wisconsin", legacyKey: "Wisconsin" },
  { id: "WY", name: "Wyoming", legacyKey: "Wyoming" },
  { id: "DC", name: "District of Columbia", legacyKey: "Washington DC" },];

/** The 51 canonical ids the programme expects, in registry order. */
export const EXPECTED_JURISDICTION_IDS: readonly JurisdictionId[] =
  JURISDICTION_IDENTITIES.map((j) => j.id);

const BY_ID = new Map<string, JurisdictionIdentity>(
  JURISDICTION_IDENTITIES.map((j) => [j.id, j])
);

const BY_LEGACY_KEY = new Map<string, JurisdictionIdentity>(
  JURISDICTION_IDENTITIES.map((j) => [j.legacyKey, j])
);

export function identityById(id: string): JurisdictionIdentity | undefined {
  return BY_ID.get(id);
}

export function identityByLegacyKey(legacyKey: string): JurisdictionIdentity | undefined {
  return BY_LEGACY_KEY.get(legacyKey);
}

/** Map a legacy display name to its canonical id. Throws on an unknown name. */
export function idForLegacyKey(legacyKey: string): JurisdictionId {
  const identity = BY_LEGACY_KEY.get(legacyKey);
  if (!identity) throw new Error(`No jurisdiction identity for legacy key "${legacyKey}"`);
  return identity.id;
}

/** Map a canonical id to its legacy display name. Throws on an unknown id. */
export function legacyKeyForId(id: JurisdictionId): string {
  const identity = BY_ID.get(id);
  if (!identity) throw new Error(`No jurisdiction identity for id "${id}"`);
  return identity.legacyKey;
}
